/*
 * Copyright (c) Nexign, JSC, 2021-2023.
 */
/**
 * Page Cashback Control component
 *
 * @author Yakov Gushchin
 * @author Julia Ignatieva
 * @author Ilya Babanov
 * @version 1.23.0
 * @since 1.12.0
 */

import React, { Fragment, useCallback, useState } from 'react';

import isNumber from 'lodash/isNumber';
import { connect } from 'react-redux';
import { Dispatch } from 'redux';
import { createStructuredSelector } from 'reselect';

import { icons } from '@/main/assets';
import { fonts } from '@/main/theme';
import { LinkBlock } from '@components/common';
import { ImgStyled } from '@components/common/Block/Img';
import { ELinkTarget } from '@components/common/ExternalLink/ExternalLink';
import LinkBlockAnchor from '@components/common/LinkBlock/LinkBlockAnchor';
import ArrowTooltip from '@components/common/Tooltip/Tooltip';
import { pushGTMAnalytic } from '@modules/analytics/GoogleTagManager/GTMActions';
import GTMOperations from '@modules/analytics/GoogleTagManager/GTMOperations';
import { IGTMPushAnalyticPayload } from '@modules/analytics/GoogleTagManager/types/GTMActionTypes';
import {
    makeSelectPageCashbackTranslation,
    makeSelectTranslationCommon,
} from '@modules/internationalization/intSelectors';
import { ICommonTranslation } from '@modules/internationalization/types';
import { IPageCashbackTranslation } from '@modules/internationalization/types/pages/cashback';
import { IRootReducer } from '@reducers/rootReducer';
import declination from '@utils/declination';
import { formatBalanceWithoutTrailingZeros } from '@utils/formatText';
import { stringFormatParams } from '@utils/stringFormat';
import useScrollPercentage from '@utils/useScrollPercentage';

import { CASHBACK_AUTO_RESERVE_ELEMENT_ID } from '../../pageCashbackConstants';
import { IPageCashbackReducer } from '../../pageCashbackReducer';
import { makeSelectCashbackMeta, makeSelectCashbackSelectCurrent } from '../../selectors/cashbackSelectors';
import CashbackNotAvailable from '../CashbackNotAvailable';
import { CashbackControlStyled } from '../styled/CashbackControlStyled';
import { CashbackTextStyled } from '../styled/CashbackStyled';
import CashbackControlServiceTabs from './CashbackControlServiceTabs';

const CashbackControl = (props: IConnectedProps & IDispatchProps) => {
    const { meta, pushGtmAnalytic, translationCashback, translationCommon } = props;
    const tabTranslation = translationCashback.tabControl;

    const scrollPercentages = [40, 60, 80, 100];
    const [scrollPercentage, setScrollPercentage] = useState(0);

    useScrollPercentage((_, resultScrollPercentage: number) => {
        if (resultScrollPercentage > scrollPercentage) {
            let index = scrollPercentages.length - 1;
            for (; index >= 0; index--) {
                const value = scrollPercentages[index];
                if (value > scrollPercentage && value <= resultScrollPercentage) {
                    setScrollPercentage(value);
                    pushGtmAnalytic({
                        operation: GTMOperations.cashback.scrollCashbackControlPage(value),
                        withAccountType: true,
                        withSusrId: true,
                        withUserRole: true,
                    });
                    break;
                }
            }
        }
    });

    const onLinkClick = useCallback(
        (link: string) => {
            pushGtmAnalytic({
                operation: GTMOperations.cashback.clickOnLinksInCashbackPage(
                    translationCashback.sideMenu.control,
                    link,
                ),
                withAccountType: true,
                withSusrId: true,
                withUserRole: true,
            });
        },
        [pushGtmAnalytic, translationCashback.sideMenu.control],
    );

    const onDisplayScoreReservedTooltip = useCallback(() => {
        pushGtmAnalytic({
            operation: GTMOperations.cashback.displayScoreReservedTooltip(),
            withAccountType: true,
            withSusrId: true,
            withUserRole: true,
        });
    }, [pushGtmAnalytic]);

    const onAutoSpendingLinkHandler = useCallback(() => {
        onLinkClick(tabTranslation.autoSpending.anchorLink);
    }, [onLinkClick, tabTranslation.autoSpending.anchorLink]);

    const renderBalance = useCallback(
        (balance?: number) =>
            !isNumber(balance) ? (
                tabTranslation.scoreError
            ) : (
                <Fragment>
                    {formatBalanceWithoutTrailingZeros(balance)}&nbsp;
                    {declination(balance, translationCommon.scores)}
                </Fragment>
            ),
        [tabTranslation, translationCommon],
    );

    if (!meta.isCashbackAvailable) {
        return (
            <CashbackNotAvailable
                translation={translationCashback.notAvailable}
                urlHowToGetCashback={meta.urlHowToGetCashback}
                onClick={onLinkClick}
            />
        );
    }

    return (
        <CashbackControlStyled>
            <div className="cashback-control__group cashback-control__score-balance">
                <CashbackTextStyled>{tabTranslation.scoreBalanceSubtitle}</CashbackTextStyled>
                <CashbackTextStyled data-name="cashbackBalance" font={fonts.textMainBold}>
                    {renderBalance(meta.balance)}
                </CashbackTextStyled>
            </div>

            {!!meta.balanceLost && (
                <div className="cashback-control__group cashback-control__score-balance-lost">
                    <CashbackTextStyled font={fonts.textSmall}>
                        {stringFormatParams(tabTranslation.scoreBalanceLost, { date: meta.balanceLost.lostAfter })}
                    </CashbackTextStyled>
                    <CashbackTextStyled data-name="cashbackBalanceLost" font={fonts.textSmall}>
                        {renderBalance(meta.balanceLost.amount)}
                    </CashbackTextStyled>
                </div>
            )}

            <div className="cashback-control__group cashback-control__score-reserved">
                <CashbackTextStyled>
                    {tabTranslation.scoreReservedSubtitle}
                    <ArrowTooltip
                        placement="right"
                        title={tabTranslation.scoreReservedTooltip}
                        titleAlign="center"
                        onOpen={onDisplayScoreReservedTooltip}
                    >
                        <ImgStyled className="score-reserved__icon small" src={icons.infoBlueIcon} />
                    </ArrowTooltip>
                </CashbackTextStyled>
                <CashbackTextStyled data-name="cashbackBalanceReserved" font={fonts.textMainBold}>
                    {renderBalance(meta.balanceReserved)}
                </CashbackTextStyled>
            </div>

            <CashbackTextStyled className="cashback-control__score-distributed">
                {meta.isAutoSpendingEnabled
                    ? tabTranslation.autoSpending.statusEnabled
                    : tabTranslation.autoSpending.statusDisabled}
                &nbsp;
                <LinkBlockAnchor url={`#${CASHBACK_AUTO_RESERVE_ELEMENT_ID}`} onClick={onAutoSpendingLinkHandler}>
                    {tabTranslation.autoSpending.anchorLink}
                </LinkBlockAnchor>
            </CashbackTextStyled>

            <CashbackTextStyled className="cashback-control__score-info">
                {tabTranslation.scoreInfo}
                <br />
                <div
                    onClick={() => onLinkClick(tabTranslation.autoSpending.howToGetCashbackLink)}
                    onMouseDown={(event: React.MouseEvent) => {
                        if (event.button !== 1) return;
                        onLinkClick(tabTranslation.autoSpending.howToGetCashbackLink);
                    }}
                >
                    <LinkBlock isExternalUrl={true} target={ELinkTarget.BLANK} url={meta.urlHowToSpentCashback}>
                        {tabTranslation.autoSpending.howToGetCashbackLink}
                    </LinkBlock>
                </div>
            </CashbackTextStyled>

            <CashbackControlServiceTabs
                translationCashback={translationCashback}
                translationCommon={translationCommon}
            />
        </CashbackControlStyled>
    );
};

export interface IConnectedProps extends Pick<IPageCashbackReducer, 'accountSelectCurrent' | 'meta'> {
    translationCashback: IPageCashbackTranslation;
    translationCommon: ICommonTranslation;
}

export interface IDispatchProps {
    pushGtmAnalytic: (payload: IGTMPushAnalyticPayload) => void;
}

export const mapStateToProps = createStructuredSelector<IRootReducer, IConnectedProps>({
    accountSelectCurrent: makeSelectCashbackSelectCurrent(),
    meta: makeSelectCashbackMeta(),
    translationCashback: makeSelectPageCashbackTranslation(),
    translationCommon: makeSelectTranslationCommon(),
});

export const mapDispatchToProps = (dispatch: Dispatch): IDispatchProps => ({
    pushGtmAnalytic: (payload: IGTMPushAnalyticPayload) => dispatch(pushGTMAnalytic(payload)),
});

export default connect(mapStateToProps, mapDispatchToProps)(CashbackControl);
